import * as THREE from "three";
import debug from "debug";

const log = debug("app:walls");

/**
 * Class representing the grid creation and processing for a 3D scene.
 * It marks grid cells, identifies edges and corners, and handles objects within the grid.
 * @category Experience
 * @subcategory GeometryManagement
 */
class MakeGrid {
  /**
   * Constructs the MakeGrid instance.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    log("MakeGrid initialized");
  }

  /**
   * Initializes the grid creation process.
   * @returns {Object} - An object containing the grid origin and the processed outer grid (oGrid).
   */
  init() {
    const { grid, gridOrigin, objects } = this.createGrid();
    this.markGridCells({ grid, gridOrigin, objects });
    const oGrid = this.identifyCornersAndEdges(grid);
    log("Outer grid created", { gridOrigin, oGrid });
    return { gridOrigin, oGrid };
  }
  cleanup() {
    console.log(`Cleaning up MakeGrid instance`);

    // Reset stored references
    this.scene = null;
    this.experience = null;

    console.log(`MakeGrid cleanup completed.`);
  }

  /**
   * Creates a grid based on the bounding box of scene objects.
   * @returns {Object} - The grid, its origin, and the relevant objects in the scene.
   */
  createGrid() {
    const bBox = new THREE.Box3();
    const objects = [];

    // Collect bounding box and relevant objects
    this.scene.traverse((object) => {
      if (
        object.name === "XMO" &&
        object.userData.layer >= 30 &&
        object.userData.layer <= 40 &&
        object.userData.layer !== 35
      ) {
        log("object", object.userData.w, object.userData.h);
        objects.push(object);

        const boundingBoxChild = object.getObjectByName("bBox");
        if (boundingBoxChild) {
          bBox.union(new THREE.Box3().setFromObject(boundingBoxChild));
        } else {
          const box = new THREE.Box3().setFromObject(object);
          bBox.union(box);
        }
      }
    });

    log("box", bBox);

    // Calculate grid size and origin based on bounding box
    const gridOrigin = new THREE.Vector3(
      Math.round(bBox.min.x) - 1,
      0,
      Math.round(bBox.min.z) - 1
    );
    const gridSizeX = Math.ceil(bBox.max.x - bBox.min.x) + 2;
    const gridSizeZ = Math.ceil(bBox.max.z - bBox.min.z) + 2;

    log("grid settings", gridOrigin, gridSizeX, gridSizeZ);

    // Initialize grid with 0s
    const grid = Array.from({ length: gridSizeZ }, () =>
      Array(gridSizeX).fill(0)
    );

    this.scene.traverse((object) => {
      if (object.name === "XMO" && object.userData.layer === 35) {
        this.markGridForLayer35(object, grid, gridOrigin);
      }
    });

    return { grid, gridOrigin, objects };
  }

  /**
   * Marks grid cells as occupied based on the objects' bounding boxes.
   * @param {Object} params - Parameters containing the grid, its origin, and objects.
   * @param {Array} params.grid - The grid to mark.
   * @param {THREE.Vector3} params.gridOrigin - The origin of the grid.
   * @param {Array} params.objects - The list of objects in the scene.
   */
  markGridCells({ grid, gridOrigin, objects }) {
    log("makeGridCells", { grid, gridOrigin, objects });
    // Mark occupied cells in the grid
    objects.forEach((object) => {
      const { w, h } = object.userData; // Assuming width and height are in userData
      const box = new THREE.Box3().setFromObject(object);

      // Get the object's world quaternion
      const quaternion = new THREE.Quaternion();
      object.getWorldQuaternion(quaternion); // Get the world quaternion

      const euler = new THREE.Euler();
      euler.setFromQuaternion(quaternion);

      // Convert Y rotation to degrees
      const rotationY = THREE.MathUtils.radToDeg(euler.y);

      // Swap width and height if rotated by 90 or 270 degrees
      let objectWidth = w;
      let objectHeight = h;
      if (Math.abs(rotationY % 180) === 90) {
        objectWidth = h;
        objectHeight = w;
      }

      const minX = Math.round(box.min.x - gridOrigin.x);
      const maxX = minX + objectWidth;
      const minZ = Math.round(box.min.z - gridOrigin.z);
      const maxZ = minZ + objectHeight;

      for (let z = minZ; z < maxZ; z++) {
        for (let x = minX; x < maxX; x++) {
          if (z >= 0 && z < grid.length && x >= 0 && x < grid[0].length) {
            grid[z][x] = 1; // Mark cell as occupied
          }
        }
      }
    });
  }

  /**
   * Marks grid cells for objects on layer 35 with a "+" symbol.
   * @param {THREE.Object3D} object - The object to mark on the grid.
   * @param {Array} grid - The grid to update.
   * @param {THREE.Vector3} gridOrigin - The origin of the grid.
   */
  markGridForLayer35(object, grid, gridOrigin) {
    const { w, h } = object.userData; // Width and height
    const box = new THREE.Box3().setFromObject(object);

    // Handle rotation, as in the original code
    const quaternion = new THREE.Quaternion();
    object.getWorldQuaternion(quaternion); // Get the world quaternion
    const euler = new THREE.Euler();
    euler.setFromQuaternion(quaternion);
    const rotationY = THREE.MathUtils.radToDeg(euler.y);

    // Adjust width and height for rotated objects
    let objectWidth = w;
    let objectHeight = h;
    if (Math.abs(rotationY % 180) === 90) {
      objectWidth = h;
      objectHeight = w;
    }

    const minX = Math.round(box.min.x - gridOrigin.x);
    const maxX = minX + objectWidth;
    const minZ = Math.round(box.min.z - gridOrigin.z);
    const maxZ = minZ + objectHeight;

    // Mark the grid with "+" instead of "1"
    for (let z = minZ; z < maxZ; z++) {
      for (let x = minX; x < maxX; x++) {
        if (z >= 0 && z < grid.length && x >= 0 && x < grid[0].length) {
          grid[z][x] = "+"; // Mark cell with "+"
        }
      }
    }
  }

  /**
   * Extracts a 3x3 kernel of values from the grid centered at a specific cell.
   * @param {Array} grid - The grid to extract the kernel from.
   * @param {number} x - The x-coordinate of the center cell.
   * @param {number} z - The z-coordinate of the center cell.
   * @returns {Array} - The 3x3 kernel.
   */
  get3x3Kernel(grid, x, z) {
    const kernel = [];
    for (let dz = -1; dz <= 1; dz++) {
      const row = [];
      for (let dx = -1; dx <= 1; dx++) {
        const newX = x + dx;
        const newZ = z + dz;
        if (
          newZ >= 0 &&
          newZ < grid.length &&
          newX >= 0 &&
          newX < grid[0].length
        ) {
          row.push(grid[newZ][newX]);
        } else {
          row.push(0); // Edge of grid assumed as 0
        }
      }
      kernel.push(row);
    }
    return kernel;
  }

  /**
   * Identifies corners and edges in the grid.
   * @param {Array} grid - The grid to process.
   * @returns {Array} - The updated grid with identified corners and edges.
   */
  identifyCornersAndEdges(grid) {
    const oGrid = grid.map((row) => row.slice());

    for (let z = 1; z < grid.length - 1; z++) {
      for (let x = 1; x < grid[z].length - 1; x++) {
        if (grid[z][x] === 1) {
          const kernel = this.get3x3Kernel(grid, x, z);

          // Check for top-left corner pattern
          if (
            kernel[0][1] === 1 &&
            kernel[1][0] === 1 &&
            kernel[0][0] === 1 &&
            kernel[1][1] === 1 &&
            kernel[2][2] === 0
          ) {
            oGrid[z + 1][x + 1] = "o";
          }

          // Repeat pattern checks for other corners...
          // Check for top-right corner pattern
          if (
            kernel[0][1] === 1 &&
            kernel[1][2] === 1 &&
            kernel[0][2] === 1 &&
            kernel[1][1] === 1 &&
            kernel[2][0] === 0
          ) {
            oGrid[z + 1][x - 1] = "o";
          }

          // Check for bottom-left corner pattern
          if (
            kernel[2][1] === 1 &&
            kernel[1][0] === 1 &&
            kernel[2][0] === 1 &&
            kernel[1][1] === 1 &&
            kernel[0][2] === 0
          ) {
            oGrid[z - 1][x + 1] = "o";
          }

          // Check for bottom-right corner pattern
          if (
            kernel[2][1] === 1 &&
            kernel[1][2] === 1 &&
            kernel[2][2] === 1 &&
            kernel[1][1] === 1 &&
            kernel[0][0] === 0
          ) {
            oGrid[z - 1][x - 1] = "o";
          }

          // Check for edges
          if (kernel[1][0] === 0) oGrid[z][x - 1] = "o"; // Left edge
          if (kernel[1][2] === 0) oGrid[z][x + 1] = "o"; // Right edge
          if (kernel[0][1] === 0) oGrid[z - 1][x] = "o"; // Top edge
          if (kernel[2][1] === 0) oGrid[z + 1][x] = "o"; // Bottom edge
        }
      }
    }

    return oGrid;
  }
}

export default MakeGrid;
